import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Typography,
  Paper,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  CircularProgress,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import moment from "moment";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import FinanceSummaryCard from "../../components/FinanceSummaryCard";

const formatBRL = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  filters: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
  stageRow: {
    marginBottom: theme.spacing(1.5),
  },
  stageBarTrack: {
    height: 22,
    borderRadius: 6,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.05)",
    overflow: "hidden",
  },
  stageBarFill: {
    height: "100%",
    borderRadius: 6,
    minWidth: 4,
    transition: "width 0.4s ease",
  },
  stageMeta: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 2,
  },
  reasonRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
}));

const CrmReports = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const [pipelines, setPipelines] = useState([]);
  const [users, setUsers] = useState([]);
  const [pipelineId, setPipelineId] = useState("");
  const [userId, setUserId] = useState("");
  const [dateFrom, setDateFrom] = useState(
    moment().subtract(30, "days").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [{ data: pipelinesData }, { data: usersData }] =
          await Promise.all([api.get("/pipelines"), api.get("/users/")]);
        setPipelines(pipelinesData);
        setUsers(usersData.users || []);
      } catch (err) {
        toastError(err);
      }
    };
    loadFilters();
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/opportunities/report", {
        params: {
          pipelineId: pipelineId || undefined,
          userId: userId || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        },
      });
      setReport(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [pipelineId, userId, dateFrom, dateTo]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const summary = report?.summary;
  const maxPassed = Math.max(
    1,
    ...(report?.stages || []).map((s) => s.passedCount)
  );

  return (
    <MainContainer>
      <MainHeader>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <IconButton
            size="small"
            onClick={() => history.push("/crm")}
            title="Voltar ao funil"
          >
            <ArrowBackIcon />
          </IconButton>
          <Title>Relatórios do CRM</Title>
        </div>
        <MainHeaderButtonsWrapper>
          <IconButton size="small" onClick={fetchReport} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* filtros */}
        <div className={classes.filters}>
          <TextField
            label="De"
            type="date"
            size="small"
            variant="outlined"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            variant="outlined"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {pipelines.length > 1 && (
            <FormControl size="small" variant="outlined" style={{ minWidth: 140 }}>
              <Select
                value={pipelineId}
                onChange={(e) => setPipelineId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">Funil padrão</MenuItem>
                {pipelines.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" variant="outlined" style={{ minWidth: 160 }}>
            <Select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">Todos os responsáveis</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {loading && !report ? (
          <div className={classes.centered}>
            <CircularProgress />
          </div>
        ) : !report ? null : (
          <>
            {/* resumo */}
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FinanceSummaryCard
                  label="Negócios no período"
                  value={String(summary.created)}
                  caption={formatBRL(summary.createdValue)}
                  icon={<TrendingUpIcon />}
                  tone="blue"
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FinanceSummaryCard
                  label="Ganhos"
                  value={String(summary.won)}
                  caption={formatBRL(summary.wonValue)}
                  icon={<CheckCircleOutlineIcon />}
                  tone="green"
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FinanceSummaryCard
                  label="Perdidos"
                  value={String(summary.lost)}
                  caption={formatBRL(summary.lostValue)}
                  icon={<HighlightOffIcon />}
                  tone="red"
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FinanceSummaryCard
                  label="Em aberto"
                  value={String(summary.open)}
                  caption={formatBRL(summary.openValue)}
                  icon={<HourglassEmptyIcon />}
                  tone="orange"
                  loading={loading}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              {/* funil por estágio */}
              <Grid item xs={12} md={7}>
                <Paper variant="outlined" className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    Funil por estágio
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: "block", marginBottom: 12 }}
                  >
                    Barra: quantos negócios do período passaram pelo estágio.
                    "No estágio": onde estão agora.
                  </Typography>
                  {report.stages.map((stage) => (
                    <div key={stage.id} className={classes.stageRow}>
                      <div className={classes.stageMeta}>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600, color: stage.color }}
                        >
                          {stage.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {stage.passedCount} passaram · {stage.currentCount} no
                          estágio ({formatBRL(stage.currentValue)})
                          {stage.avgDaysInStage != null &&
                            ` · ${stage.avgDaysInStage}d médios`}
                        </Typography>
                      </div>
                      <div className={classes.stageBarTrack}>
                        <div
                          className={classes.stageBarFill}
                          style={{
                            width: `${(stage.passedCount / maxPassed) * 100}%`,
                            backgroundColor: stage.color || "#9E9E9E",
                          }}
                        />
                      </div>
                    </div>
                  ))}

                  <Divider style={{ margin: "16px 0" }} />
                  <Typography variant="body2" color="textSecondary">
                    Taxa de conversão:{" "}
                    <strong>
                      {summary.conversionRate != null
                        ? `${summary.conversionRate}%`
                        : "— (nenhum negócio fechado no período)"}
                    </strong>
                    {summary.avgDaysToClose != null && (
                      <>
                        {" "}
                        · Tempo médio até ganhar:{" "}
                        <strong>{summary.avgDaysToClose} dias</strong>
                      </>
                    )}
                  </Typography>
                </Paper>
              </Grid>

              {/* motivos de perda + por responsável */}
              <Grid item xs={12} md={5}>
                <Paper variant="outlined" className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    Motivos de perda
                  </Typography>
                  {report.lostReasons.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      Nenhum negócio perdido no período. 🎉
                    </Typography>
                  ) : (
                    report.lostReasons.map((item) => (
                      <div key={item.reason} className={classes.reasonRow}>
                        <Typography variant="body2">{item.reason}</Typography>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {item.count}
                        </Typography>
                      </div>
                    ))
                  )}
                </Paper>

                <Paper variant="outlined" className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    Por responsável
                  </Typography>
                  <div style={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Responsável</TableCell>
                          <TableCell align="center">Abertos</TableCell>
                          <TableCell align="center">Ganhos</TableCell>
                          <TableCell align="center">Perdidos</TableCell>
                          <TableCell align="right">Receita ganha</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.byUser.map((row) => (
                          <TableRow key={row.userId || "none"}>
                            <TableCell>{row.userName}</TableCell>
                            <TableCell align="center">{row.open}</TableCell>
                            <TableCell align="center">{row.won}</TableCell>
                            <TableCell align="center">{row.lost}</TableCell>
                            <TableCell align="right">
                              {formatBRL(row.wonValue)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default CrmReports;
